import React, { useState, useEffect } from 'react';
import { Menu, X, Globe, Sun, Moon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { useTheme } from '../ThemeContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const { language, changeLanguage, t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: t.nav.home, href: '#home' },
    { name: t.nav.about, href: '#about' },
    { name: t.nav.projects, href: '#projects' },
    { name: t.nav.contact, href: '#contact' }
  ];

  const languages = [
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
  ];

  const currentLang = languages.find(l => l.code === language);

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled
          ? isDark
            ? 'bg-[#0a0f1a]/90 backdrop-blur-xl border-b border-white/5'
            : 'bg-white/90 backdrop-blur-xl border-b border-gray-200 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-6 py-4 max-w-7xl">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#home" className="group flex items-center gap-3">
            <div className="w-1 h-12 bg-blue-500 rounded-full"></div>
            <div className="flex flex-col leading-tight">
              <span className={`text-lg font-bold tracking-wide group-hover:text-blue-400 transition-colors duration-300 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Muhammed Salih
              </span>
              <span className={`text-lg font-bold tracking-wide group-hover:text-blue-400 transition-colors duration-300 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                DEDE
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item, index) => (
              <a
                key={index}
                href={item.href}
                className={`relative px-5 py-2 transition-all duration-300 group font-light ${
                  isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {item.name}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-blue-500 transition-all duration-300 group-hover:w-2/3 rounded-full"></span>
              </a>
            ))}
          </div>

          {/* Right Side - Theme Toggle, Language Switcher & CTA */}
          <div className="hidden md:flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2.5 rounded-xl transition-all duration-300 ${
                isDark
                  ? 'text-gray-400 hover:text-white hover:bg-white/5'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              title={isDark ? 'Light Mode' : 'Dark Mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className={`flex items-center gap-2 px-3 py-2 transition-colors rounded-lg ${
                  isDark
                    ? 'text-gray-400 hover:text-white hover:bg-white/5'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm">{currentLang?.flag} {currentLang?.code.toUpperCase()}</span>
              </button>

              {isLangMenuOpen && (
                <div className={`absolute top-full right-0 mt-2 backdrop-blur-xl rounded-xl border overflow-hidden min-w-[140px] ${
                  isDark
                    ? 'bg-[#0a0f1a]/95 border-white/10'
                    : 'bg-white border-gray-200 shadow-lg'
                }`}>
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setIsLangMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        language === lang.code
                          ? 'bg-blue-500/20 text-blue-400'
                          : isDark
                            ? 'text-gray-400 hover:text-white hover:bg-white/5'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Button */}
            <a
              href="#contact"
              className="relative overflow-hidden bg-blue-600 text-white px-6 py-2.5 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/20"
            >
              <span className="relative z-10">{t.nav.letsConnect}</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 rounded-xl transition-colors ${
              isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-100'
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className={`md:hidden mt-4 backdrop-blur-xl rounded-2xl border overflow-hidden ${
            isDark
              ? 'bg-[#0a0f1a]/95 border-white/5'
              : 'bg-white border-gray-200 shadow-lg'
          }`}>
            <div className="p-4 space-y-2">
              {navItems.map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  className={`block px-4 py-3 rounded-xl transition-all duration-300 font-light ${
                    isDark
                      ? 'text-gray-400 hover:text-white hover:bg-white/5'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </a>
              ))}

              {/* Mobile Theme & Language */}
              <div className="flex gap-2 px-4 py-3">
                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className={`py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                    isDark
                      ? 'text-gray-400 hover:text-white bg-white/5'
                      : 'text-gray-600 hover:text-gray-900 bg-gray-100'
                  }`}
                >
                  {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>

                {/* Language buttons */}
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                    }}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      language === lang.code
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                        : isDark
                          ? 'text-gray-400 hover:text-white bg-white/5'
                          : 'text-gray-600 hover:text-gray-900 bg-gray-100'
                    }`}
                  >
                    {lang.flag}
                  </button>
                ))}
              </div>

              <a
                href="#contact"
                className="block px-4 py-3 mt-4 text-center bg-blue-600 text-white rounded-xl font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                {t.nav.letsConnect}
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
